/**
 * Intelligence Notes Component
 * ============================
 * Displays AI-generated insights, anomalies, and recommendations
 */

import React, { useState } from 'react';

import {
  Lightbulb,
  AlertTriangle,
  TrendingUp,
  Link2,
  Target,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Zap,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';

export default function IntelligenceNotes({
  insights,
  anomalies,
  trends,
  correlations,
  kpis,
  onQueryRequest,
}) {
  const [expandedSections, setExpandedSections] = useState({
    insights: true,
    anomalies: false,
    kpis: false,
    correlations: false,
  });

  const toggleSection = section => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Check if we have any data
  const hasData =
    (insights?.length || 0) +
      (anomalies?.length || 0) +
      (kpis?.length || 0) +
      (correlations?.length || 0) >
    0;

  if (!hasData) {
    return (
      <div className="intelligence-empty">
        <Lightbulb className="w-12 h-12 text-slate-300" />
        <p>No insights available yet</p>
        <span className="hint">Insights will be generated after Excel analysis completes</span>
      </div>
    );
  }

  return (
    <div className="intelligence-notes">
      {/* Key Insights */}
      {insights && insights.length > 0 && (
        <CollapsibleSection
          title="Key Insights"
          icon={Lightbulb}
          count={insights.length}
          expanded={expandedSections.insights}
          onToggle={() => toggleSection('insights')}
        >
          <div className="insights-list">
            {insights.map((insight, idx) => (
              <InsightCard key={idx} insight={insight} />
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Anomalies */}
      {anomalies && anomalies.length > 0 && (
        <CollapsibleSection
          title="Anomalies Detected"
          icon={AlertTriangle}
          count={anomalies.length}
          expanded={expandedSections.anomalies}
          onToggle={() => toggleSection('anomalies')}
          badgeColor="warning"
        >
          <div className="anomalies-list">
            {anomalies.slice(0, 10).map((anomaly, idx) => (
              <AnomalyCard key={idx} anomaly={anomaly} />
            ))}
            {anomalies.length > 10 && (
              <div className="more-items">+{anomalies.length - 10} more anomalies</div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* KPIs */}
      {kpis && kpis.length > 0 && (
        <CollapsibleSection
          title="Key Performance Indicators"
          icon={Target}
          count={kpis.length}
          expanded={expandedSections.kpis}
          onToggle={() => toggleSection('kpis')}
        >
          <div className="kpis-grid">
            {kpis
              .filter(k => k.type === 'total' || k.type === 'growth')
              .slice(0, 8)
              .map((kpi, idx) => (
                <KPICard key={idx} kpi={kpi} />
              ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Correlations */}
      {correlations && correlations.length > 0 && (
        <CollapsibleSection
          title="Correlations Found"
          icon={Link2}
          count={correlations.length}
          expanded={expandedSections.correlations}
          onToggle={() => toggleSection('correlations')}
        >
          <div className="correlations-list">
            {correlations.slice(0, 5).map((corr, idx) => (
              <CorrelationCard key={idx} correlation={corr} />
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

function CollapsibleSection({
  title,
  icon: Icon,
  count,
  expanded,
  onToggle,
  children,
  badgeColor,
}) {
  return (
    <div className={`collapsible-section ${expanded ? 'expanded' : ''}`}>
      <button className="section-header" onClick={onToggle}>
        <div className="section-title">
          <Icon className="w-4 h-4" />
          <span>{title}</span>
          <span className={`section-badge ${badgeColor || ''}`}>{count}</span>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {expanded && <div className="section-content">{children}</div>}
    </div>
  );
}

function InsightCard({ insight }) {
  const getPriorityIcon = priority => {
    switch (priority) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'positive':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'high':
        return <Zap className="w-4 h-4 text-amber-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <div className={`insight-card priority-${insight.priority || 'info'}`}>
      <div className="insight-icon">{getPriorityIcon(insight.priority)}</div>
      <div className="insight-content">
        <span className="insight-type">{insight.type}</span>
        <p className="insight-text">{insight.text}</p>
      </div>
    </div>
  );
}

function AnomalyCard({ anomaly }) {
  return (
    <div className={`anomaly-card severity-${anomaly.severity}`}>
      <div className="anomaly-header">
        <span className="anomaly-metric">{anomaly.metric}</span>
        <span className={`severity-badge ${anomaly.severity}`}>{anomaly.severity}</span>
      </div>
      <p className="anomaly-message">{anomaly.message}</p>
      <div className="anomaly-details">
        <span className="detail">Value: {formatValue(anomaly.value)}</span>
        <span className="detail">Expected: {formatValue(anomaly.expected)}</span>
        {anomaly.zScore && <span className="detail">{anomaly.zScore.toFixed(1)}σ deviation</span>}
      </div>
    </div>
  );
}

function KPICard({ kpi }) {
  const isGrowth = kpi.type === 'growth';
  const isPositive = isGrowth && kpi.value > 0;
  const isNegative = isGrowth && kpi.value < 0;

  return (
    <div
      className={`kpi-card ${isGrowth ? (isPositive ? 'positive' : isNegative ? 'negative' : '') : ''}`}
    >
      <div className="kpi-header">
        <span className="kpi-name">{kpi.name}</span>
        {isGrowth &&
          (isPositive ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : isNegative ? (
            <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />
          ) : null)}
      </div>
      <div className="kpi-value">{kpi.formatted || formatValue(kpi.value)}</div>
      {kpi.comparison && <div className="kpi-comparison">{kpi.comparison}</div>}
    </div>
  );
}

function CorrelationCard({ correlation }) {
  const strengthClass =
    correlation.strength === 'strong'
      ? 'strong'
      : correlation.strength === 'moderate'
        ? 'moderate'
        : 'weak';

  return (
    <div className={`correlation-card ${strengthClass}`}>
      <div className="correlation-metrics">
        <span className="metric">{correlation.metric1}</span>
        <ArrowRight className="w-4 h-4" />
        <span className="metric">{correlation.metric2}</span>
      </div>
      <div className="correlation-details">
        <span className={`direction ${correlation.direction}`}>{correlation.direction}</span>
        <span className="strength">{correlation.strength}</span>
        <span className="coefficient">r = {correlation.correlation.toFixed(2)}</span>
      </div>
      <p className="interpretation">{correlation.interpretation}</p>
    </div>
  );
}

function formatValue(value) {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') {
    if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
}
